import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Menu, X, ChevronDown, Shield, Sparkles, Sun, Phone, Droplets,
  Wrench, Network, Camera, Handshake,
  Map, Mail,
  Banknote, MessageSquare, HelpCircle,
} from "lucide-react";
import Logo from "@/components/layout/Logo";

// ─── Dropdown definitions ─────────────────────────────────────────────────
const PRODUCTS = [
  { to: "/hot-water-heat-pumps",     label: "Hot Water Heat Pumps", desc: "Smarter, efficient hot water for NZ homes",  icon: Droplets, category: true },
  { to: "/neopower-black-shield",    label: "NeoPower Black Shield", desc: "Premium all-in-one heat pump",               icon: Shield,   primary: true },
  { to: "/aeromax-270l",             label: "AeroMax 270L",          desc: "Coming soon — early interest list",          icon: Sparkles, badge: "SOON" },
  { to: "/solar-battery",            label: "Solar + Battery",       desc: "Next phase — register interest",             icon: Sun,      badge: "NEXT" },
];

const INSTALLATION = [
  { to: "/installation",                 label: "Installation Process",     desc: "How an AeroMax install actually works",        icon: Wrench },
  { to: "/installer-network",            label: "Installer Network",        desc: "Trusted local installers across NZ",           icon: Network },
  { to: "/recent-installs",              label: "Recent Installs",          desc: "Real Kiwi homes & case studies",               icon: Camera },
  { to: "/become-installer-partner",     label: "Become an Installer Partner", desc: "Grow your business with AeroMax",           icon: Handshake },
];

const HOME_SHOWS = [
  { to: "/home-shows",                       label: "All Home Shows",   desc: "Every region in one view, with a jump-to bar", icon: Map, primary: true },
  { to: "/home-shows#register-interest",     label: "Register Interest", desc: "Get a heads-up when AeroMax is exhibiting near you", icon: Mail },
];

const SUPPORT = [
  { to: "/nz-green-loan-guide", label: "NZ Green Loan Guide", desc: "NZ bank green loan options & how to apply", icon: Banknote },
  { to: "/contact",             label: "Contact Us",          desc: "Talk to the AeroMax team",                  icon: MessageSquare },
  { to: "/faqs",                label: "FAQs",                desc: "Common questions, clearly answered",        icon: HelpCircle },
];

// ─── Reusable hover/click dropdown ────────────────────────────────────────
function Dropdown({ label, testId, items, width = 420, currentPath }) {
  const [open, setOpen] = useState(false);
  const isActive = items.some((i) => i.to && i.to.split("#")[0] === currentPath);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1 ${
          isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#F1F5F9]"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`${testId}-menu`}
          className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
          style={{ width }}
        >
          <div className="rounded-2xl bg-white border border-[#E2E8F0] shadow-2xl shadow-black/5 p-3">
            {items.map((p) => {
              const target = p.to;
              const slug = target.replace(/[/#\s]/g, "-").replace(/^-+/, "");
              const active = target.split("#")[0] === currentPath;
              const Icon = p.icon || Sparkles;
              return (
                <Link
                  key={target}
                  to={target}
                  data-testid={`${testId}-link-${slug}`}
                  className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
                    p.primary ? "bg-[#10B981]/5 ring-1 ring-[#10B981]/20" : ""
                  } ${active ? "bg-[#10B981]/10" : "hover:bg-[#F8FAFC]"}`}
                >
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${
                    p.primary ? "bg-[#10B981] text-white"
                      : p.category ? "bg-white border border-[#10B981]/30 text-[#10B981]"
                      : "bg-[#0B132B] text-white"
                  }`}>
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div className="flex-1">
                    <p className="font-heading font-semibold text-[#0B132B] flex items-center gap-2">
                      {p.label}
                      {p.primary && (
                        <span className="px-2 py-0.5 rounded-full bg-[#10B981] text-white text-[10px] font-bold tracking-wide">
                          LAUNCH
                        </span>
                      )}
                      {p.category && (
                        <span className="px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-wide">
                          CATEGORY
                        </span>
                      )}
                      {p.badge && (
                        <span className="px-2 py-0.5 rounded-full bg-[#0B132B]/8 text-[#0B132B] text-[10px] font-bold tracking-wide">
                          {p.badge}
                        </span>
                      )}
                    </p>
                    {p.desc && <p className="text-xs text-[#475569] mt-0.5">{p.desc}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileGroup({ label, testId, items, currentPath }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`mobile-${testId}-toggle`}
        className="flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-[#0B132B] hover:bg-[#F1F5F9]"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 flex flex-col gap-1">
          {items.map((p) => {
            const target = p.to;
            const Icon = p.icon || Sparkles;
            const active = target.split("#")[0] === currentPath;
            return (
              <Link
                key={target}
                to={target}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium ${
                  active ? "bg-[#10B981]/10 text-[#0B132B]" : "text-[#475569] hover:bg-[#F1F5F9]"
                }`}
              >
                <Icon className="h-4 w-4 text-[#10B981]" />
                {p.label}
                {p.badge && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#059669] font-semibold">
                    {p.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────
export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname, hash]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]" : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-7 h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center group shrink-0">
          <Logo size={36} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          <NavLink
            to="/"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#F1F5F9]"
              }`
            }
          >
            Home
          </NavLink>

          <Dropdown
            label="Products"
            testId="nav-products"
            items={PRODUCTS}
            width={420}
            currentPath={pathname}
          />

          <NavLink
            to="/savings-calculator"
            data-testid="nav-calculator"
            className={({ isActive }) =>
              `px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#F1F5F9]"
              }`
            }
          >
            Calculator
          </NavLink>

          <Dropdown
            label="Installation"
            testId="nav-installation"
            items={INSTALLATION}
            width={420}
            currentPath={pathname}
          />
          <Dropdown
            label="Home Shows"
            testId="nav-home-shows"
            items={HOME_SHOWS}
            width={420}
            currentPath={pathname}
          />
          <Dropdown
            label="Support"
            testId="nav-support"
            items={SUPPORT}
            width={420}
            currentPath={pathname}
          />
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 shrink-0">
          <a
            href="tel:05082376629"
            data-testid="navbar-phone-link"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] hover:border-[#10B981]/60 hover:bg-[#10B981]/5 px-3 py-2 text-sm font-semibold text-[#0B132B] whitespace-nowrap transition-all"
          >
            <Phone className="h-3.5 w-3.5 text-[#10B981] shrink-0" strokeWidth={2.4} />
            <span className="tracking-tight">0508 AEROMAX</span>
          </a>
          <Link
            to="/free-home-assessment"
            data-testid="navbar-quote-btn"
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-4 py-2 text-sm font-semibold whitespace-nowrap transition-all hover:shadow-lg hover:shadow-emerald-500/30 active:scale-95"
          >
            Get Quote
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E2E8F0] text-[#0B132B] shrink-0"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-medium ${
                  isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#F1F5F9]"
                }`
              }
            >
              Home
            </NavLink>

            <MobileGroup label="Products"     testId="products"     items={PRODUCTS}             currentPath={pathname} />

            <NavLink
              to="/savings-calculator"
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-medium ${
                  isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#F1F5F9]"
                }`
              }
            >
              Savings Calculator
            </NavLink>

            <MobileGroup label="Installation"  testId="installation" items={INSTALLATION}         currentPath={pathname} />
            <MobileGroup label="Home Shows"    testId="home-shows"   items={HOME_SHOWS}           currentPath={pathname} />
            <MobileGroup label="Support"       testId="support"      items={SUPPORT}              currentPath={pathname} />

            <Link
              to="/free-home-assessment"
              className="mt-2 px-4 py-3 rounded-xl bg-[#10B981] text-white text-center font-semibold"
            >
              Get Quote
            </Link>
            <a
              href="tel:05082376629"
              data-testid="mobile-phone-link"
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#E2E8F0] text-[#0B132B] font-semibold"
            >
              <Phone className="h-4 w-4 text-[#10B981]" strokeWidth={2.4} />
              0508 AEROMAX
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
